import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button, Col, Form, Radio, Row, Select, Space, Upload } from 'antd';
import axios from 'axios';
import PropTypes from 'prop-types';

import CountrySeeder from '../../../components/CountrySeeder/CountrySeeder';
import FloatingLabel from '../../../components/FloatingLabel/FloatingLabel';

import { AddCircle, ArrowRight, ChevronDown, ChevronUp, Gallery, Trash } from '../../../assets/icons';
import { getPreSignedUrl, updateUserProfile } from '../../../services/authApi/userApi';
import { addUserData } from '../../../store/reducer/accountSlice';
import { ALLOWED_FORMATS, FILE_UPLOAD_VALIDATION, validateFileSize } from '../../../utils/fileValidation';
import { preventReload } from '../../../utils/preventReload';
import { toastNotify } from '../../../utils/toastNotify';

const StepOne = (props) => {
  const [citizen, setCitizen] = useState();
  const [identificationType, setIdentificationType] = useState();
  const [fileListFront, setFileListFront] = useState([]);
  const [fileListBack, setFileListBack] = useState([]);
  const [fileListPassport, setFileListPassport] = useState([]);
  const [country, setCountry] = useState();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [arrowUp, setArrowUp] = useState(false);
  const [fileValidate, setFileValidate] = useState(false); // for invalid file type
  const [fileSizeValidate, setFileSizeValidate] = useState(false); // for invalid file size
  const [fileFrontValidate, setFileFrontValidate] = useState(false);
  const [fileFrontSizeValidate, setFileFrontSizeValidate] = useState(false);
  const [fileBackValidate, setFileBackValidate] = useState(false);
  const [fileBackSizeValidate, setFileBackSizeValidate] = useState(false);
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const counter = useSelector((state) => state.userData.counter);
  useEffect(() => {
    preventReload(form);
  }, []);

  const onChangeCitizen = (e) => {
    setCitizen(e.target.value);
  };
  const onChangeFront = ({ fileList: newFileList }) => {
    setFileListFront(newFileList);
  };
  const onChangeBack = ({ fileList: newFileList }) => {
    setFileListBack(newFileList);
  };
  const onChangePassport = ({ fileList: newFileList }) => {
    setFileListPassport(newFileList);
  };

  const beforeUpload = async (file, value) => {
    const validSize = validateFileSize(file?.size);

    if (!ALLOWED_FORMATS.includes(file.type)) {
      if (value === 'front') {
        setFileFrontValidate(true);
      } else if (value === 'back') {
        setFileBackValidate(true);
      } else {
        setFileValidate(true);
      }
    } else if (!validSize) {
      if (value === 'front') {
        setFileFrontSizeValidate(true);
      } else if (value === 'back') {
        setFileBackSizeValidate(true);
      } else {
        setFileSizeValidate(true);
      }
    }
    return false;
  };

  const handleRemoveUploaded = (fileId, value) => {
    if (identificationType === 'passport') {
      setFileListPassport((prevFileList) => prevFileList.filter((item) => item.uid !== fileId));
      setFileValidate(false);
      setFileSizeValidate(false);
    } else {
      setFileListFront((prevFileList) => prevFileList.filter((item) => item.uid !== fileId));
      setFileListBack((prevFileList) => prevFileList.filter((item) => item.uid !== fileId));
      if (value === 'front') {
        setFileFrontSizeValidate(false);
        setFileFrontValidate(false);
      } else if (value === 'back') {
        setFileBackSizeValidate(false);
        setFileBackValidate(false);
      }
    }
  };

  const onPreSignedUrlSuccess = async (resp, data, values) => {
    const preSignedUrl = resp?.data?.url;
    const storagePath = resp?.data?.storagePath;

    const config = {
      headers: {
        'Content-Type': data?.fileType,
        'Access-Control-Allow-Origin': '*',
      },
    };

    const formData = new FormData();
    formData.append('image', data?.file);

    try {
      // Upload the file to S3 using the obtained pre-signed URL
      let type, frontUrl, backUrl;
      await axios.put(preSignedUrl, data?.file, config);

      let docs;
      if (data?.displayView === 'Front') {
        type = data?.docType;
        frontUrl = storagePath;
      } else {
        type = data?.docType;
        backUrl = storagePath;
      }

      const stepOneFormData = {
        usIdentity: values.citizenShip === 'yes' ? true : false,
        docs,
      };

      return { docs: { type, frontUrl, backUrl }, usIdentity: values.citizenShip === 'yes' ? true : false };
    } catch (error) {
      // Handle any errors that occur during file upload
      const { response } = error;
      toastNotify('error', response?.data?.message || 'Error during file upload.');
      setLoading(false);
    }
  };

  const onPreSignedUrlError = (error) => {
    const errorMessage = error?.response?.data?.message || 'An error occurred while uploading your document. Please try again';
    toastNotify('error', errorMessage);
    setLoading(false);
  };

  const handleUpdateUserProfile = async (citizenShip, stepOneFormData) => {
    try {
      const resp = await updateUserProfile(stepOneFormData);
      if (!resp.isError) {
        await dispatch(addUserData(resp?.data?.user));
        citizenShip === 'no' ? navigate('/') : props.nextAction('StepTwo');
      } else {
        toastNotify('error', resp?.message);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadImages = async (uploadsData, values) => {
    let frontUrl, backUrl, type, usIdentity;
    const promises = uploadsData.map(async (data) => {
      try {
        const resp = await getPreSignedUrl(data.fileType, data.docType, data.displayView);
        const preSignedResp = await onPreSignedUrlSuccess(resp, data, values);
        type = preSignedResp?.docs?.type;
        usIdentity = preSignedResp?.usIdentity;
        if (preSignedResp?.docs?.frontUrl) {
          frontUrl = preSignedResp?.docs?.frontUrl;
        }
        if (preSignedResp?.docs?.backUrl) {
          backUrl = preSignedResp?.docs?.backUrl;
        }
      } catch (error) {
        onPreSignedUrlError(error);
      }
    });

    await Promise.all(promises);

    return {
      docs: {
        frontUrl,
        backUrl,
        type,
      },
      usIdentity,
    };
  };
  const onStepOneSubmit = async (values) => {
    setLoading(true);

    if (values.citizenShip === 'no') {
      if (country) {
        const stepOneFormData = {
          usIdentity: false,
          countryId: country?.toString(),
        };
        handleUpdateUserProfile(values.citizenShip, stepOneFormData);
      } else {
        navigate('/');
      }
    } else {
      const uploadsData = [];
      if (identificationType === 'passport') {
        const passportPreSinged = {
          fileType: fileListPassport[0]?.name?.split('.')?.pop()?.toLowerCase(),
          docType: 'Passport',
          displayView: 'Front',
          file: fileListPassport[0]?.originFileObj,
        };
        uploadsData.push(passportPreSinged);
      } else {
        if (fileListFront.length > 0) {
          const frontPreSinged = {
            fileType: fileListFront[0]?.name?.split('.')?.pop()?.toLowerCase(),
            docType: 'Driving License',
            displayView: 'Front',
            file: fileListFront[0]?.originFileObj,
          };
          uploadsData.push(frontPreSinged);
        }
        if (fileListBack.length > 0) {
          const backPreSinged = {
            fileType: fileListBack[0]?.name?.split('.')?.pop()?.toLowerCase(),
            docType: 'Driving License',
            displayView: 'Back',
            file: fileListBack[0]?.originFileObj,
          };
          uploadsData.push(backPreSinged);
        }
      }

      const imageUploadResp = await uploadImages(uploadsData, values);
      handleUpdateUserProfile(values.citizenShip, imageUploadResp);
    }
  };
  return (
    <>
      {counter > 1 ? (
        <>
          <p>you have already been verified please go to home</p>
          <Button href='/' type='primary'>
            Realbricks Marketplace
          </Button>
        </>
      ) : (
        <Form
          form={form}
          className='auth-form stepone'
          name='stepOneForm'
          onFinish={onStepOneSubmit}
          // onFinishFailed={onFinishFailed}
        >
          <p>Are you a U.S. Citizen or Permanent Resident?</p>
          <Form.Item name='citizenShip'>
            <Radio.Group disabled={loading} value={citizen} onChange={onChangeCitizen}>
              <Space direction='vertical' size='large'>
                <Radio value='yes' className='fw-600'>
                  Yes
                </Radio>
                <Radio value='no' className='fw-600'>
                  No
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          {citizen === 'yes' && (
            <>
              <p>Please upload a valid government issued ID, must be clear and legible. (JPEG, JPG, PNG files only)</p>
              <FloatingLabel label='Type of identification' inputId='identification' inputValue={identificationType}>
                <>
                  <div id='identification-anchor' />
                  <Form.Item name='identification'>
                    <Select
                      disabled={loading}
                      id='identification'
                      onChange={(value) => setIdentificationType(value)}
                      value={identificationType}
                      getPopupContainer={() => document.getElementById('identification-anchor')}
                      onDropdownVisibleChange={() => setArrowUp(!arrowUp)}
                      suffixIcon={arrowUp ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
                    >
                      <Select.Option value='passport'>Passport</Select.Option>
                      <Select.Option value='license'>Driver's License</Select.Option>
                    </Select>
                  </Form.Item>
                </>
              </FloatingLabel>
              {identificationType === 'license' && (
                <Row gutter={20}>
                  <Col span={24} md={12}>
                    <label htmlFor='licenseFront' className='sr-only'>
                      Upload front of ID
                    </label>
                    <Form.Item className='license-front'>
                      <Upload
                        id='licenseFront'
                        accept='image/png, image/jpeg, image/jpg'
                        onChange={onChangeFront}
                        showUploadList={{
                          showRemoveIcon: false,
                          showPreviewIcon: false,
                        }}
                        fileList={fileListFront}
                        className='avatar-uploader'
                        listType='picture-card'
                        beforeUpload={(value) => beforeUpload(value, 'front')}
                      >
                        {fileListFront.length < 1 && (
                          <div>
                            <AddCircle />
                            <p className='fw-600'>Upload front of ID</p>
                          </div>
                        )}
                      </Upload>
                      {fileListFront[0] && (
                        <div className='filename'>
                          <span>
                            <Gallery verticalAlign='middle' marginRight='7px' />
                            {fileListFront[0].name}
                          </span>
                          <Trash verticalAlign='middle' className='delete-icon' onClick={() => handleRemoveUploaded(fileListFront[0].uid, 'front')} />
                        </div>
                      )}
                      {fileFrontValidate === true && (
                        <p className='error' style={{ color: 'red', paddingTop: 10 }}>
                          {FILE_UPLOAD_VALIDATION.FORMAT}
                        </p>
                      )}
                      {fileFrontSizeValidate === true && <p style={{ color: 'red', paddingTop: 10 }}>{FILE_UPLOAD_VALIDATION.FILE_SIZE}</p>}
                    </Form.Item>
                  </Col>
                  <Col span={24} md={12}>
                    <label htmlFor='licenseBack' className='sr-only'>
                      Upload back of ID
                    </label>
                    <Form.Item className='license-back'>
                      <Upload
                        id='licenseBack'
                        accept='image/png, image/jpeg, image/jpg'
                        onChange={onChangeBack}
                        showUploadList={{
                          showRemoveIcon: false,
                          showPreviewIcon: false,
                        }}
                        fileList={fileListBack}
                        className='avatar-uploader'
                        listType='picture-card'
                        beforeUpload={(value) => beforeUpload(value, 'back')}
                      >
                        {fileListBack.length < 1 && (
                          <div>
                            <AddCircle />
                            <p className='fw-600'>Upload back of ID</p>
                          </div>
                        )}
                      </Upload>
                      {fileListBack[0] && (
                        <div className='filename'>
                          <span>
                            <Gallery verticalAlign='middle' marginRight='7px' />
                            {fileListBack[0].name}
                          </span>
                          <Trash verticalAlign='middle' className='delete-icon' onClick={() => handleRemoveUploaded(fileListBack[0].uid, 'back')} />
                        </div>
                      )}
                      {fileBackValidate === true && (
                        <p className='error' style={{ color: 'red', paddingTop: 10 }}>
                          {FILE_UPLOAD_VALIDATION.FORMAT}
                        </p>
                      )}
                      {fileBackSizeValidate === true && <p style={{ color: 'red', paddingTop: 10 }}>{FILE_UPLOAD_VALIDATION.FILE_SIZE}</p>}
                    </Form.Item>
                  </Col>
                </Row>
              )}
              {identificationType === 'passport' && (
                <Row>
                  <Col span={24} md={12}>
                    <label htmlFor='passport' className='sr-only'>
                      Upload ID
                    </label>
                    <Form.Item className='passport'>
                      <Upload
                        id='passport'
                        accept='image/png, image/jpeg, image/jpg'
                        onChange={onChangePassport}
                        fileList={fileListPassport}
                        className='avatar-uploader'
                        listType='picture-card'
                        beforeUpload={(value) => beforeUpload(value, 'passport')}
                        showUploadList={{
                          showRemoveIcon: false,
                          showPreviewIcon: false,
                        }}
                      >
                        {fileListPassport.length < 1 && (
                          <div>
                            <AddCircle />
                            <p className='fw-600'>Upload ID</p>
                          </div>
                        )}
                      </Upload>
                      {fileListPassport[0] && (
                        <div className='filename'>
                          <span>
                            <Gallery verticalAlign='middle' marginRight='7px' />
                            {fileListPassport[0].name}
                          </span>
                          <Trash
                            verticalAlign='middle'
                            className='delete-icon'
                            onClick={() => !loading && handleRemoveUploaded(fileListPassport[0].uid, 'passport')}
                          />
                        </div>
                      )}
                      {fileValidate === true && (
                        <p className='error' style={{ color: 'red', paddingTop: 10 }}>
                          {FILE_UPLOAD_VALIDATION.FORMAT}
                        </p>
                      )}
                      {fileSizeValidate === true && <p style={{ color: 'red', paddingTop: 10 }}>{FILE_UPLOAD_VALIDATION.FILE_SIZE}</p>}
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </>
          )}
          {citizen === 'no' && (
            <div className='not-citizen'>
              <p className='subhead-2'>Country not supported</p>
              <p>
                We can only accept United States citizens as investors at this time. We are actively working to allow investors from other countries.
                Select yout country and we will notify when your country is available.
              </p>
              <Form.Item className='mb-0'>
                <CountrySeeder country={country} setCountry={(value) => setCountry(value)} />
              </Form.Item>
            </div>
          )}
          {props.nextAction}
          {/* <Form.Item className='auth-form__fixed-submit'> */}
          {citizen === 'yes' && (
            <Form.Item className='auth-form__fixed-submit'>
              <Button
                type='primary'
                htmlType='submit'
                className='ant-btn-primary--alt'
                loading={loading}
                disabled={
                  (identificationType === 'passport' ? fileListPassport.length === 0 : (fileListFront.length && fileListBack.length) === 0) ||
                  fileValidate ||
                  fileSizeValidate ||
                  fileFrontSizeValidate ||
                  fileBackSizeValidate ||
                  fileFrontValidate ||
                  fileBackValidate
                }
              >
                Continue <ArrowRight marginLeft='10px' verticalAlign='middle' top='-2px' />
              </Button>
            </Form.Item>
          )}
          {citizen === 'no' && (
            <Form.Item className='auth-form__fixed-submit'>
              <Button htmlType='submit' type='primary' className='ant-btn-primary--alt'>
                Back to Realbricks Marketplace
              </Button>
            </Form.Item>
          )}
          {/* </Form.Item> */}
        </Form>
      )}
    </>
  );
};

StepOne.propTypes = {
  nextAction: PropTypes.func,
};

export default StepOne;
